import React, { useCallback, useMemo, useRef, useState } from "react";

import { Grid, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ShipeeziGrid, {
  Column as ShipeeziColumn,
  SortColumn,
  SortType,
} from "Components/AppGrid/shipeezi-new-grid";
import classnames from "classnames";
import { format } from "date-fns";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { FiEdit, FiUpload } from "react-icons/fi";
import { MdOutlineChangeCircle } from "react-icons/md";
import { Badge } from "reactstrap";

import { useAgentHook } from "../../../../../../../modules/moving/external-agent/hooks/useAgentHook";
import { AgentServiceBooking } from "../../../../../../../models/agent.service.booking";
import { PriceGroup } from "../../../../../../../models/price.group";
import {
  ChargeOptions,
  Service as ServiceBooking,
  ChargeOptionsEnum,
} from "../../../../../../../models/service";
import { useAppState } from "../../../../../../../context/StateContext";
import { Status } from "../../../../../../../util/enums";
import ActionButtons from "../../../../../../../components/Generic/ActionButtons";
import PriceGroupService from "../../../../../../../services/price.group.service";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import EditingPrice, { EditingPriceHandles } from "./EditingPrice";
import useStyles from "./styles";
import CurrencyService from "../../../../../../../services/currency.service";
import { Currency } from "../../../../../../../models/currency";
import ServiceBookingService from "../../../../../../../services/service.booking.service";
import {
  formatCurrency,
  getFeePropertyByServiceDefaultFee,
  getRateByChargeSelected,
} from "../../../../../../../util/prices";
import { Button } from "../../../../../../../components/Form";
import { Measurement } from "../../../../../../../models/measurement";

import { Color } from "../../../../../../../components/Form/Button";
import UploadPrices, { UploadPricesHandles } from "./UploadPrices";
import { paginate } from "../../../../../../../util/paginate";
import { getMeasurement } from "../../../../../../../util/getMeasurement";

enum IconColor {
  DEFAULT = "#475467",
  SUCCESS = "#32D583",
  DANGER = "#F97066",
  DISABLED = "#98A2B3",
}

export type ValueOf<T> = T[keyof T];

export interface PricingColumn extends ShipeeziColumn {
  name: keyof AgentServiceBooking;
  title: string;
}

interface PricingProps {
  onSave: () => void;
  onCancel: () => void;
  isUpdating?: boolean;
  viewOnly?: boolean;
}

export const Pricing: React.FC<PricingProps> = ({ onSave, onCancel, isUpdating, viewOnly }) => {
  const { currentUser } = useAppState();

  const currency = currentUser?.company?.currency;
  const measurement = getMeasurement();

  const { agent, handleUpdateAgent } = useAgentHook();
  const classes = useStyles();
  const { t } = useTranslation();

  const [allGroups, setAllGroups] = useState<PriceGroup[]>([]);
  const [allCurrencies, setAllCurrencies] = useState<Currency[]>([]);
  const [allServices, setAllServices] = useState<ServiceBooking[]>([]);
  const [rowsSelected, setRowsSelected] = useState<string[]>([]);

  const editingPriceRef = useRef<EditingPriceHandles>(null);
  const uploadPriceRef = useRef<UploadPricesHandles>(null);

  const { isFetching: loadingGroups } = useQuery(
    ["groups.list"],
    () =>
      PriceGroupService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
        include: JSON.stringify([{ association: "services" }]),
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) {
          setAllGroups(response.data);
        }
      },
    }
  );

  const { isFetching: loadingCurrencies } = useQuery(
    ["currencies.list"],
    () =>
      CurrencyService.getAll({
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
        status: Status.ACTIVE,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array)
          setAllCurrencies(response.data);
      },
    }
  );

  const { isFetching: loadingServices } = useQuery(
    ["service.list"],
    () =>
      ServiceBookingService.getAll({
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([
          ["status", "asc"],
          ["sellDescription", "asc"],
        ]),
        include: JSON.stringify([{ association: "groups" }]),
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) {
          setAllServices(response.data);
        }
      },
    }
  );

  const availableGroups = useMemo(() => {
    let groups: PriceGroup[] = [];
    const availables: PriceGroup[] = [];

    if (allServices && allServices.length > 0) {
      allServices.forEach((service) => {
        groups = [...groups, ...(service.groups || [])];
      });
    }
    groups.forEach((group) => {
      const find = availables.find((available) => available.id === group.id);

      if (!find) availables.push(group);
    });

    return availables;
  }, [allServices]);

  const loading = loadingServices || loadingGroups || loadingCurrencies;

  const getDimenstionFeeOption = useCallback((measurement?: Measurement) => {
    const fee = t("DimensionFee").replace("{dimension}", measurement?.code || "");
    const split = fee.split(" ");

    return `${split[0]?.toUpperCase()} ${split[1]}`;
  }, []);

  const chargeOptions: { key: keyof ChargeOptions; label: string }[] = useMemo(() => {
    return [
      {
        key: ChargeOptionsEnum.Fee,
        label: getDimenstionFeeOption(measurement),
      },
      { key: ChargeOptionsEnum.Flat, label: t("flatFee") },
      { key: ChargeOptionsEnum.Hourly, label: t("hourlyFee") },
      { key: ChargeOptionsEnum["Half Hour"], label: t("halfHourFee") },
    ];
  }, [measurement]);

  const getCurrencyById = useCallback(
    (currencyId?: string | number) =>
      allCurrencies.find((currency) => String(currency.id) === String(currencyId)),
    [allCurrencies]
  );
  const getServiceById = useCallback(
    (serviceId?: string | number) =>
      allServices.find((service) => String(service.id) === String(serviceId)),
    [allServices]
  );
  const getGroupById = useCallback(
    (groupId?: string | number) => allGroups.find((group) => String(group.id) === String(groupId)),
    [allGroups]
  );

  const compareAgentServiceBooking = useCallback(
    (data1?: AgentServiceBooking, data2?: AgentServiceBooking) => {
      if (!data1 || !data2) return false;
      if ((data1.groupId && !data2.groupId) || (!data1.groupId && data2.groupId)) return false;
      if ((data1.serviceId && !data2.serviceId) || (!data1.serviceId && data2.serviceId))
        return false;

      if (data1.groupId && data2.groupId) {
        return data1.groupId === data2.groupId && data1.serviceId === data2.serviceId;
      }

      return data1.serviceId === data2.serviceId;
    },
    []
  );

  const getAgentService = useCallback(
    (serviceId?: number, groupId?: number) => {
      if (!agent || !agent.services || agent.services.length <= 0 || !serviceId) return undefined;

      if (groupId) {
        return agent.services.find(
          (agentService) =>
            String(agentService.serviceId) === String(serviceId) &&
            String(agentService.groupId) === String(groupId)
        );
      }

      return agent.services.find(
        (agentService) =>
          String(agentService.serviceId) === String(serviceId) &&
          (agentService.groupId === null || agentService.groupId === undefined)
      );
    },
    [agent?.services]
  );

  const allData: AgentServiceBooking[] = useMemo(() => {
    const data: AgentServiceBooking[] = [];

    allServices.forEach((service) => {
      const propertyServiceDefaultFee = getFeePropertyByServiceDefaultFee(
        service
      ) as keyof AgentServiceBooking;

      if (service && service.groups && service.groups.length > 0) {
        service.groups.forEach((group) => {
          const agentService = getAgentService(service.id, group.id);

          data.push({
            serviceId: service.id,
            service,
            groupId: group.id,
            group,
            currencyId: currency?.id,
            currency,
            agentId: agent?.id,
            [propertyServiceDefaultFee]: getRateByChargeSelected(
              service?.defaultFee as keyof ChargeOptions,
              service
            ),
            ...agentService,
            status:
            service.status === Status.INACTIVE
              ? "Unavailable"
              : agentService?.status === Status.ACTIVE ||
                  agentService?.status === Status.INACTIVE
                ? agentService.status
                : Status.INACTIVE,
          });
        });
      } else {
        const agentService = getAgentService(service.id, undefined);

        data.push({
          serviceId: service.id,
          service,
          currencyId: currency?.id,
          currency,
          agentId: agent?.id,
          [propertyServiceDefaultFee]: getRateByChargeSelected(
            service?.defaultFee as keyof ChargeOptions,
            service
          ),
          ...agentService,
          status:
          service.status === Status.INACTIVE
            ? "Unavailable"
            : agentService?.status === Status.ACTIVE ||
                agentService?.status === Status.INACTIVE
              ? agentService.status
              : Status.INACTIVE,
        });
      }
    });

    return data;
  }, [allServices, agent?.services]);

  const handleEditButton = (id: number, data: AgentServiceBooking) => {
    if (viewOnly) return null;
    const unavailable = data?.status === ("Unavailable" as Status);
    const tooltip = unavailable ? t("thisServiceCannotBeEditedBecauseIsUnavailable") : t("edit");

    return (
      <div>
        <Tooltip data-cy="action-edit" title={tooltip} placement="top">
          <div
            onClick={() => {
              if (!viewOnly && !unavailable) {
                editingPriceRef.current?.openModal(data);
              }
            }}
          >
            <FiEdit
              color={unavailable ? IconColor.DISABLED : IconColor.DEFAULT}
              className={classnames({
                [classes.button]: !viewOnly && !unavailable,
                [classes.buttonNone]: viewOnly,
              })}
              size={18}
            />
          </div>
        </Tooltip>
      </div>
    );
  };

  const renderCustomColumn = (
    key: keyof AgentServiceBooking,
    value?: string | number,
    data?: AgentServiceBooking
  ) => {
    switch (key) {
      case "serviceId":
        return getServiceById(value)?.sellDescription || "";
      case "groupId":
        return getGroupById(value)?.name || "";
      case "charge":
        return chargeOptions.find((option) => option.key === value)?.label || "";
      case "m3Fee":
      case "flatFee":
      case "hourlyFee":
      case "halfHourFee":
        return value !== null && value !== undefined
          ? formatCurrency(value, data?.currency)
          : value;
      case "currencyId":
        return getCurrencyById(value)?.name || "";
      case "updatedAt":
        const updated = value ? new Date(value) : null;
        const custom = data?.customUpdatedAt ? new Date(data?.customUpdatedAt) : null;
        const date =
          updated && custom
            ? updated.getTime() <= custom.getTime()
              ? custom
              : updated
            : updated || custom;

        return date ? format(date, "dd/MM/yyyy hh:mm aa") : "N/A";
      case "status":
        if (!value) return null;

        return (
          <Badge
            className={classnames("badgeComponent", classes.statusBadge, {
              [classes.statusBadgeActive]: value === Status.ACTIVE,
              [classes.statusBadgeInactive]: value == Status.INACTIVE,
              [classes.statusBadgeUnavailable]: value === "Unavailable",
            })}
          >
            {value}
          </Badge>
        );
    }
  };

  const onChangeSaveStatus = (newStatus: Status.ACTIVE | Status.INACTIVE, baseData?: AgentServiceBooking) => {
    const cloneServices = _.cloneDeep(agent?.services || []) as AgentServiceBooking[];
    const service = cloneServices.find((service) => compareAgentServiceBooking(service, baseData));

    if (service) {
      service.status = newStatus;
      service.customUpdatedAt = new Date();
    } else {
      cloneServices.push({
        ...baseData,
        status: newStatus,
        customUpdatedAt: new Date(),
      });
    }
    handleUpdateAgent("services", cloneServices);
  };

  const handleChangeStatus = (id: number, data: AgentServiceBooking) => {
    if (viewOnly) return null;

    const newStatus = data?.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE;
    const unavailable = data?.status === ("Unavailable" as Status);
    const tooltip = unavailable
      ? t("thisServiceCannotBeActivatedBecauseIsUnavailable")
      : `${t("changeStatusTo")} ${newStatus}`;

    return (
      <div>
        <Tooltip title={tooltip} placement="top">
          <div
            data-cy={``}
            onClick={() => {
              if (!viewOnly && !unavailable) onChangeSaveStatus(newStatus, data);
            }}
            className={classnames({
              [classes.button]: !viewOnly && !unavailable,
              [classes.buttonNone]: viewOnly,
            })}
          >
            <MdOutlineChangeCircle
              color={unavailable ? IconColor.DISABLED : IconColor.DEFAULT}
              size={20}
            />
          </div>
        </Tooltip>
      </div>
    );
  };

  const onConfirmSaveAgentServiceBooking = (agentService: AgentServiceBooking) => {
    const cloneServices = _.cloneDeep(agent?.services || []) as AgentServiceBooking[];
    const service = cloneServices.find((service) =>
      compareAgentServiceBooking(service, agentService)
    );

    if (service) {
      const keys: Array<keyof AgentServiceBooking> = Object.keys(agentService) as Array<
        keyof AgentServiceBooking
      >;

      keys.forEach((key) => {
        service[key] = agentService[key] as any;
      });
      service.customUpdatedAt = new Date();
    } else {
      cloneServices.push({
        ...agentService,
        customUpdatedAt: new Date(),
      });
    }
    handleUpdateAgent("services", cloneServices);
    const serviceFound = allServices.find(
      (requestService) => requestService.id === agentService?.serviceId
    );

    if (serviceFound) {
      NotificationToast.success(`${serviceFound?.sellDescription} ${t("wasEditedSuccessfully")}`);
    }
  };

  const onUploadAgentServiceBookingFile = (data: AgentServiceBooking[]) => {
    handleUpdateAgent("services", data);
    NotificationToast.success(t("pricesUpdatedSuccessfully"));
  };

  const formatMeasurementFeeTitle = (columnTitle: string) => {
    if (measurement && measurement?.code) {
      const code = measurement?.code?.toUpperCase();

      return columnTitle.replace("{dimension}", code);
    }

    return columnTitle.replace("{dimension} ", "");
  };

  const columns: PricingColumn[] = [
    {
      name: "serviceId",
      title: t("service"),
      formatComponent: (value: string, data: AgentServiceBooking) =>
        renderCustomColumn("serviceId", value, data),
    },
    {
      name: "groupId",
      title: t("group"),
      formatComponent: (value: string, data: AgentServiceBooking) =>
        renderCustomColumn("groupId", value, data),
    },
    {
      name: "m3Fee",
      title: formatMeasurementFeeTitle(t("DimensionFee")),
      formatComponent: (value: string, data: AgentServiceBooking) =>
        renderCustomColumn("m3Fee", value, data),
      hideSort: true,
    },
    {
      name: "flatFee",
      title: t("flatFee"),
      formatComponent: (value: string, data: AgentServiceBooking) =>
        renderCustomColumn("flatFee", value, data),
      hideSort: true,
    },
    {
      name: "hourlyFee",
      title: t("hourlyFee"),
      formatComponent: (value: string, data: AgentServiceBooking) =>
        renderCustomColumn("hourlyFee", value, data),
      hideSort: true,
    },
    {
      name: "halfHourFee",
      title: t("halfHourFee"),
      formatComponent: (value: string, data: AgentServiceBooking) =>
        renderCustomColumn("halfHourFee", value, data),
      hideSort: true,
    },
    {
      name: "currencyId",
      title: t("currency"),
      formatComponent: (value: string, data: AgentServiceBooking) =>
        renderCustomColumn("currencyId", value, data),
    },
    {
      name: "updatedAt",
      title: t("lastUpdated"),
      formatComponent: (value: string, data: AgentServiceBooking) =>
        renderCustomColumn("updatedAt", value, data),
    },
    {
      name: "status",
      title: t("status"),
      formatComponent: (value: string, data: AgentServiceBooking) =>
        renderCustomColumn("status", value, data),
    },
  ];

  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<{ [x: string]: string[] }>({});
  const [sort, setSort] = useState<SortColumn>();

  const onSorting = (sort: SortColumn) => {
    setSort(sort);
  };

  const allDataGrid = useMemo(() => {
    let newDataGrid: AgentServiceBooking[] = allData;

    if (sort) {
      const isAsc = sort.direction === SortType.ASC;

      switch (sort.columnName) {
        case "serviceId":
          newDataGrid = allData.sort((a, b) => {
            if (
              String(a?.service?.sellDescription || "").toLowerCase() >
              String(b?.service?.sellDescription || "").toLowerCase()
            )
              return isAsc ? 1 : -1;
            if (
              String(a?.service?.sellDescription || "").toLowerCase() <
              String(b?.service?.sellDescription || "").toLowerCase()
            )
              return !isAsc ? 1 : -1;

            return 0;
          });
          break;
        case "groupId":
          newDataGrid = allData.sort((a, b) => {
            if (
              String(a?.group?.name || "").toLowerCase() >
              String(b?.group?.name || "").toLowerCase()
            )
              return isAsc ? 1 : -1;
            if (
              String(a?.group?.name || "").toLowerCase() <
              String(b?.group?.name || "").toLowerCase()
            )
              return !isAsc ? 1 : -1;

            return 0;
          });
          break;
        case "currencyId":
          newDataGrid = allData.sort((a, b) => {
            if (
              String(a?.currency?.name || "").toLowerCase() >
              String(b?.currency?.name || "").toLowerCase()
            )
              return isAsc ? 1 : -1;
            if (
              String(a?.currency?.name || "").toLowerCase() <
              String(b?.currency?.name || "").toLowerCase()
            )
              return !isAsc ? 1 : -1;

            return 0;
          });
          break;
        case "updatedAt":
          newDataGrid = allData.sort((a, b) => {
            const updatedA = a?.updatedAt ? new Date(a?.updatedAt) : null;
            const customA = a?.customUpdatedAt ? new Date(a?.customUpdatedAt) : null;
            const dateA =
              updatedA && customA
                ? updatedA.getTime() <= customA.getTime()
                  ? customA
                  : updatedA
                : updatedA || customA;

            const updatedB = b?.updatedAt ? new Date(b?.updatedAt) : null;
            const customB = b?.customUpdatedAt ? new Date(b?.customUpdatedAt) : null;
            const dateB =
              updatedB && customB
                ? updatedB.getTime() <= customB.getTime()
                  ? customB
                  : updatedB
                : updatedB || customB;

            if (!dateB) return isAsc ? 1 : -1;
            if (!dateA) return !isAsc ? 1 : -1;

            if (dateA.getTime() > dateB.getTime()) return isAsc ? 1 : -1;
            if (dateA.getTime() < dateB.getTime()) return !isAsc ? 1 : -1;

            return 0;
          });
          break;
        case "status":
          newDataGrid = allData.sort((a, b) => {
            if (String(a?.status || "").toLowerCase() > String(b?.status || "").toLowerCase())
              return isAsc ? 1 : -1;
            if (String(a?.status || "").toLowerCase() < String(b?.status || "").toLowerCase())
              return !isAsc ? 1 : -1;

            return 0;
          });
          break;
      }
    }
    if (search) {
      const lower = search.toLowerCase();

      newDataGrid = newDataGrid.filter((data) => {
        return (
          data?.group?.name?.toLowerCase()?.includes(lower) ||
          data?.service?.sellDescription?.toLowerCase()?.includes(lower) ||
          data?.currency?.name?.toLowerCase()?.includes(lower) ||
          data?.status?.toLowerCase()?.includes(lower)
        );
      });
    }
    if (filters) {
      const keys = Object.keys(filters);

      if (keys.length > 0) {
        keys.forEach((key) => {
          const options = filters[key];

          newDataGrid = newDataGrid.filter((data) => {
            if (key === "status" && data.status) {
              return options.includes(data.status);
            }
            if (key === "group") {
              const none = options.includes(t("nonGrouped"));

              return none
                ? !data.group || options.includes(String(data?.group?.name))
                : options.includes(String(data?.group?.name));
            }

            return false;
          });
        });
      }
    }

    return paginate(newDataGrid, pageNumber, pageSize);
  }, [allData, pageNumber, pageSize, search, filters, sort]);

  return (
    <Grid
      data-cy="agents-pricing"
      className={classes.container}
      container
      direction="column"
      wrap="nowrap"
    >
      <Grid item data-cy="agents-pricing-title" className={classes.header}>
        <span className={classes.title}>{t("pricing")}</span>
      </Grid>

      <ShipeeziGrid
        data={allDataGrid}
        totalItems={allDataGrid.length}
        pageSize={pageSize}
        pageSizeList={[50, 100, 150]}
        onChangePageSize={setPageSize}
        currentPage={pageNumber}
        onNextPage={(page: number) => setPageNumber(page - 1)}
        onPreviousPage={(page: number) => setPageNumber(page - 1)}
        onGoToPage={(page: number) => setPageNumber(page - 1)}
        loadingData={loading}
        keyExtractor={(row: AgentServiceBooking) => `${row.serviceId || ""}-${row.groupId || ""}`}
        columns={columns}
        positionActionsColumn="right"
        rowActionsExtra={[
          {
            title: t("edit"),
            key: "edit",
            buttonAndAction: handleEditButton,
          },
          {
            title: t("changeStatus"),
            key: "changeStatus",
            buttonAndAction: handleChangeStatus,
          },
        ]}
        emptyData={loading ? `${t("loadingPrices")}...` : t("noInformationToBeShown")}
        generalActions={{
          orderBy: {
            left: ["handleSearch", "handleFilterBy"],
            right: ["handleUploadPrice"],
          },
          handleSearch: {
            inputPlaceholder: t("typePressEnter"),
            inputSearchMode: "enter",
            onSearch: (searchValue) => {
              if (searchValue.length > 0) {
                const values = Object.values(searchValue[0]);

                setSearch(values[0]);
              } else {
                setSearch("");
              }
            },
          },
          handleFilterBy: {
            categories: [
              {
                name: t("status"),
                column: "status",
                options: [Status.ACTIVE, Status.INACTIVE],
              },
              {
                name: t("group"),
                column: "group",
                options: [
                  t("nonGrouped"),
                  ...availableGroups.map((group) => String(group.name)),
                ] as string[],
              },
            ],
            onChange: setFilters,
          },
          handleUploadPrice: (
            <Button
              tooltip={t("updatePrices")}
              customColor={Color.SECONDARY}
              data-cy="uploadPrices"
              onClick={() => uploadPriceRef.current?.openModal()}
            >
              <FiUpload size={18} className="mr-2" />
              <span style={{ whiteSpace: "nowrap" }}>{t("updatePrices")}</span>
            </Button>
          ),
        }}
        onSorting={(sorts: SortColumn[]) => {
          if (sorts && sorts.length > 0) {
            onSorting(sorts[0]);
          }
        }}
        handleSelect={(selecteds: string[]) => {
          setRowsSelected(selecteds);
        }}
      />

      <EditingPrice
        ref={editingPriceRef}
        allCurrencies={allCurrencies}
        allGroups={allGroups}
        allServices={allServices}
        onConfirmSave={onConfirmSaveAgentServiceBooking}
      />

      <UploadPrices
        ref={uploadPriceRef}
        agentServices={allData.filter(
          (data) => data.status === Status.ACTIVE || data.status === Status.INACTIVE
        )}
        onUploadAgentServiceBookingFile={onUploadAgentServiceBookingFile}
      />

      <div className={classes.buttons}>
        <ActionButtons
          cancelDisabled={isUpdating}
          loading={isUpdating}
          dataCyCancel={"agents-cancel-button"}
          dataCySave={"agents-save-button"}
          onCancel={onCancel}
          onSave={onSave}
          showSaveButton={!viewOnly}
        />
      </div>
    </Grid>
  );
};
